<template>
  <el-form ref="form" label-width="50px" class="time-entry-dialog">
    <el-form-item label="Time" class="date-time" v-if="!multiEdit">
      <el-date-picker v-model="date" type="date" placeholder="Date" @change="dateChanged" :picker-options="pickerOptions" ref="date"></el-date-picker>
      <el-time-picker v-model="start" default-value="start" format="HH:mm" ref="start" @focus="timePickerFocused"></el-time-picker>
      <el-time-picker v-model="end" placeholder="End" format="HH:mm" ref="end" @focus="timePickerFocused"></el-time-picker>
    </el-form-item>
    <el-form-item label="Task" >
      <el-collapse v-model="activeNames" @change="triggerTaskSelector">
        <el-collapse-item :title="task ? `${client || ' - - - '} : ${projectPath} : ${task.attributes.name}` : 'Select a task'" name="1" :class="{ placeholder: !task }">
          <TaskTable :onSelect="selectTask" :isActivityVisible="adminMode && isAdmin" v-if="!options.taskIsDisabled" ref="taskTable"></TaskTable>
        </el-collapse-item>
      </el-collapse>
      <el-tooltip content="Selected time entries include different tasks" placement="top" v-if="!tasksConcur">
        <font-awesome-icon icon="exclamation-circle" class="warning-icon"/>
      </el-tooltip>
    </el-form-item>
    <el-form-item label="Activity">
      <el-col :span="8" class="activity">
        <el-select v-model="activity" value-key="attributes.name" @visible-change="activityChanged" placeholder="Select an activity" no-data-text="No activities available" ref="activity">
          <el-option v-for="activity in activities" :key="activity.id" :label="`${activity.attributes.name}${defaultActivity && activity.attributes.name === defaultActivity.attributes.name ? '*' : ''}`" :value="activity" ></el-option>
        </el-select>
        <el-tooltip content="Selected time entries include different activities" placement="top" v-if="!activitiesConcur">
          <font-awesome-icon icon="exclamation-circle" class="warning-icon"/>
        </el-tooltip>
      </el-col>
      <el-col :span="3" class="description-label">
        Description
      </el-col>
      <el-col :span="13">
        <el-input v-model="description" :class="'entry-description'" ref="description"></el-input>
        <el-tooltip content="Selected time entries include different descriptions" placement="top" v-if="!descriptionsConcur">
          <font-awesome-icon icon="exclamation-circle" class="warning-icon"/>
        </el-tooltip>
      </el-col>
    </el-form-item>
    <el-form-item label="User" v-if="adminMode && isAdmin && !multiEdit">
      <el-col :span="8" class="user">
        <el-select v-model="user" value-key="attributes.name" placeholder="Select a user"> 
          <el-option
            v-for="user in users"
            :key="user.id"
            :label="user.attributes.name"
            :value="user"
            >
          </el-option>
        </el-select>
      </el-col>
      <el-col :span="3" class="submitted-label">
        Submitted
      </el-col>
      <el-col :span="1">
        <el-checkbox v-model="submitted"></el-checkbox>
      </el-col>
      <el-col :span="2">
        Locked
      </el-col>
      <el-col :span="10">
        <el-checkbox v-model="locked"></el-checkbox>
      </el-col>
    </el-form-item>
    <el-form-item label="Submitted" v-if="adminMode && isAdmin && multiEdit">
      <el-col :span="2">
        <el-checkbox v-model="submitted"></el-checkbox>
        <el-tooltip content="Entries consist of different submitted states" placement="top" v-if="!submittedConcur">
          <font-awesome-icon icon="exclamation-circle" class="warning-icon"/>
        </el-tooltip>
      </el-col>
      <el-col :span="2">
        Locked
      </el-col>
      <el-col :span="20">
        <el-checkbox v-model="locked"></el-checkbox>
        <el-tooltip content="Entries consist of different locked states" placement="top" v-if="!lockedConcur">
          <font-awesome-icon icon="exclamation-circle" class="warning-icon"/>
        </el-tooltip>
      </el-col>
    </el-form-item>
    <el-form-item class="buttons">
      <el-button @click="cancel">Cancel</el-button>
      <el-button type="primary" @click="confirmSave" :disabled="!saveIsEnabled" ref="save">Save</el-button>
    </el-form-item>
    <el-dialog title="Overlapping Time Entries" :visible.sync="confirmOverlapDialogIsVisible" width="420px" class="confirm-overlap-dialog" append-to-body>
      <div class="warning"></div>
      <span>{{confirmOverlapDialogMessage}}</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmOverlapDialogIsVisible = false" size="small">Cancel</el-button>
        <el-button @click="save"  size="small">OK</el-button>
        <el-button type="primary" @click="adjustTimeEntry"  size="small">Adjust</el-button>
      </span>
    </el-dialog>
  </el-form>
</template>

<script>
import moment from 'moment'
import TaskTable from '@/components/TaskTable'
import { dateToDbUtcString, formatDateString, formatTimeString } from '@/utils/timeEntry'
import { singularize } from '@/utils/utils'
import { getUserRole } from '@/utils/users'
import { getTimeRemaining } from '@/utils/store'

export default {

  components: {
    TaskTable
  },
  props: {
    options: Object
  },
  data () {
    let entries = this.options.timeEntries
    let original = JSON.parse(JSON.stringify(entries[0]))
    let tasksConcur = entries.every(entry => (!entry.task && !entries[0].task) || entry.task.id === entries[0].task.id)
    let activitiesConcur = entries.every(entry => (!entry.activity && !entries[0].activity) || entry.activity.id === entries[0].activity.id)
    let descriptionsConcur = entries.every(entry => entry.description === entries[0].description)
    let lockedConcur = entries.every(entry => entry.locked === entries[0].locked)
    let submittedConcur = entries.every(entry => entry.submitted === entries[0].submitted)
    let activitiesArrays = entries.map(entry => entry.validActivities || [])
    let activities = activitiesArrays.shift().filter(activity => activitiesArrays.every(activityArray => activityArray.some(a => a.id === activity.id))) || []
    let multiEdit = entries.length > 1
    let adminMode = this.$store.getters.adminMode
    let isAdmin = this.$store.state.authentication.user.role.attributes.name === 'admin'

    activities = activities.filter(function (el) {
      return el != null
    })

    return {
      activeNames: [],
      timeEntries: entries,
      multiEdit: multiEdit,
      date: original.date,
      start: original.start,
      end: original.end,
      tasksConcur: tasksConcur,
      task: tasksConcur ? original.task : null,
      client: original.client,
      projectPath: original.projectPath,
      activitiesConcur: activitiesConcur,
      activity: activitiesConcur ? original.activity : {id: null},
      activities: activities,
      defaultActivity: original.defaultActivity,
      descriptionsConcur: descriptionsConcur,
      description: descriptionsConcur ? original.description : '',
      submittedConcur: submittedConcur,
      submitted: submittedConcur ? original.submitted : undefined,
      lockedConcur: lockedConcur,
      locked: lockedConcur ? original.locked : undefined,
      user: original.user,
      confirmOverlapDialogIsVisible: false,
      confirmOverlapDialogMessage: '',
      pickerOptions: {
        showOverflowTooltip: false,
        firstDayOfWeek: this.$store.getters.firstDayOfWeek,
        disabledDate (time) {
          let disabled = adminMode && isAdmin ? false : time.getTime() > Date.now() + 3600 * 1000 * 24 * 14 || time.getTime() < Date.now() - 3600 * 1000 * 24 * 14
          return disabled
        },
        shortcuts: [
          {
            text: 'Today',
            onClick (picker) {
              picker.$emit('pick', new Date())
            }
          },
          {
            text: 'Yesterday',
            onClick (picker) {
              const date = new Date()
              date.setTime(date.getTime() - 3600 * 1000 * 24)
              picker.$emit('pick', date)
            }
          },
          {
            text: 'A week ago',
            onClick (picker) {
              const date = new Date()
              date.setTime(date.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', date)
            }
          }
        ]
      }
    }
  },
  computed: {
    adminMode () {
      return this.$store.getters.adminMode
    },
    isAdmin () {
      return this.$store.state.authentication.user.role.attributes.name === 'admin'
    },
    saveIsEnabled () {
      return this.multiEdit || (this.date && this.start && this.user)
    },
    users () {
      let users = this.$store.getters['users/all']
      users = users.filter(function (user) { return !user.attributes.isDeleted && getUserRole(user).attributes.name !== 'disconnected' })
      return users.sort((a, b) => {
        var textA = a.attributes.name.toUpperCase()
        var textB = b.attributes.name.toUpperCase()
        return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
      })
    }
  },
  mounted () {
    if (this && this.$refs && this.$refs['taskTable']) {
      let focus = this.options.focus
      let focusElement = this.$refs[focus]

      this.activeNames = focus === 'task' ? ['1'] : []
      focus === 'task' && this.$nextTick(() => this.$refs['taskTable'].$refs[focus].focus())
      focusElement && focusElement.focus()
    }
  },
  created () {
    document.addEventListener('keypress', this.keypress)
  },
  destroyed () {
    document.removeEventListener('keypress', this.keypress)
  },
  methods: {
    keypress (e) {
      e.keyCode === 13 && this.confirmOverlapDialogIsVisible && this.adjustTimeEntry()
      e.keyCode === 13 && !e.srcElement.closest('.el-message-box__wrapper') && this.confirmSave()
    },
    activityChanged (isOpen) {
      !isOpen && setTimeout(() => {
        if (this.$refs && this.$refs.activity) {
          this.$refs.activity.blur()
        }
        window.getSelection().removeAllRanges()
      }, 100)
    },
    dateChanged (date) {
      if (moment(date).isBefore(moment().subtract(14, 'days')) || moment(date).isAfter(moment().add(7, 'days'))) {
        this.$confirm(`The selected date is outside of the typical allowed date range. Do you wish to continue?`, 'Restricted Date', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => { }).catch(() => {
          this.date = this.timeEntries[0].date
        })
      }
    },
    triggerTaskSelector (activeNames) {
      activeNames.length && this.$nextTick(() => this.$refs.taskTable.$refs.task.focus())
    },
    timePickerFocused (timePicker) {
      setTimeout(() => timePicker.$el.firstElementChild.select(), 80)
    },
    cancel () {
      this.$emit('close')
      this.options.cancel && this.options.cancel()
    },
    confirmSave () {
      if (this.multiEdit) {
        this.$confirm(`Multiple time entries are about to be modified. Do you wish to continue?`, 'Confirm Save', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => { this.confirmSaveLocked() }).catch(() => { })
      } else {
        this.confirmSaveLocked()
      }
    },
    confirmSaveLocked () {
      let timeEntryIsLocked = (this.lockedConcur && this.locked && this.timeEntries[0].locked) || (!this.lockedConcur && this.locked === undefined && this.timeEntries.some(entry => entry.locked)) || (!this.lockedConcur && this.locked && this.timeEntries.some(entry => entry.locked))

      if (timeEntryIsLocked) {
        let message = this.multiEdit ? 'One or more of these time entries are locked.' : 'This time entry is locked.'
        this.$confirm(`${message} Do you wish to continue?`, `Locked Time ${this.multiEdit ? 'Entries' : 'Entry'}`, {
          confirmButtonText: 'Cancel',
          cancelButtonText: 'OK',
          type: 'warning'
        }).then(() => { }).catch(() => this.confirmSaveOverlap())
      } else {
        this.confirmSaveOverlap()
      }
    },
    confirmSaveOverlap () {
      let entries = this.$store.getters.timeEntries
      let entry = this.timeEntries[0]
      let index = entries.indexOf(entry)
      let date = moment(this.date)

      this.start = moment(this.start).set({ 'year': date.get('year'), 'month': date.get('month'), 'date': date.get('date') })
      this.end = this.end ? moment(this.end).set({ 'year': date.get('year'), 'month': date.get('month'), 'date': date.get('date') }) : this.end

      if (!this.multiEdit && index >= 0) {
        let overlapsPrevious = index !== 0 && entry.user === entries[index - 1].user && this.start.isBefore(entries[index - 1].end) && !this.start.isBefore(entries[index - 1].start)
        let overlapsNext = index < entries.length - 1 && entry.user === entries[index + 1].user && this.end && this.end.isAfter(entries[index + 1].start) && !this.end.isAfter(entries[index + 1].end)

        if (overlapsPrevious || overlapsNext) {
          this.confirmOverlapDialogMessage = `This time entry overlaps the ${overlapsPrevious ? 'previous' : 'following'} time entry. Select OK to continue or adjust the ${overlapsPrevious ? 'previous' : 'following'} time entry.`
          this.confirmOverlapDialogIsVisible = true
        } else {
          this.save()
        }
      } else {
        this.save()
      }
    },
    adjustTimeEntry () {
      let entries = this.$store.getters.timeEntries
      let entry = this.timeEntries[0]
      let index = entries.indexOf(entry)
      let overlapsPrevious = index !== 0 && entry.user === entries[index - 1].user && moment(this.start).isBefore(entries[index - 1].end) && !moment(this.start).isBefore(entries[index - 1].start)
      let entryToAdjust = overlapsPrevious ? entries[index - 1] : entries[index + 1]
      let propertyToAdjust = overlapsPrevious ? 'end' : 'start'
      let adjustedValue = dateToDbUtcString(overlapsPrevious ? this.start : this.end)

      entryToAdjust.attributes[propertyToAdjust] = adjustedValue
      this.$store.dispatch('time-entries/update', entryToAdjust).then(() => { this.$store.dispatch('time-entries/loadById', { id: entryToAdjust.id }) })
      this.save()
    },
    save () {
      let multiEdit = this.timeEntries.length > 1
      let date = moment(this.date)
      let year = date.get('year')
      let month = date.get('month')
      let day = date.get('date')
      let start = moment(this.start).set({ 'year': year, 'month': month, 'date': day })
      let end = this.end && moment(this.end).set({ 'year': year, 'month': month, 'date': day })

      this.confirmOverlapDialogIsVisible = false
      this.timeEntries.forEach(entry => {
        let newEntry = { type: 'time-entries', attributes: {}, relationships: { activity: { data: null }, event: { data: null }, milestone: { data: null }, notes: { data: [] }, task: { data: null }, user: { data: null } } }

        if (entry.id) newEntry.id = entry.id
        newEntry.attributes.start = multiEdit ? entry.attributes.start : dateToDbUtcString(start)
        if (end) newEntry.attributes.end = multiEdit ? entry.attributes.end : dateToDbUtcString(end)
        newEntry.attributes.isLocked = !this.lockedConcur && this.locked === undefined ? entry.locked : this.locked
        newEntry.attributes.isSubmitted = !this.submittedConcur && this.submitted === undefined ? entry.submitted : this.submitted
        newEntry.attributes.description = !this.descriptionsConcur && !this.description ? entry.description || '' : this.description || ''
        newEntry.attributes.durationInHours = entry.duration || 0
        newEntry.relationships.activity.data = !this.activitiesConcur && !this.activity ? entry.activity : this.activity
        newEntry.relationships.user.data = multiEdit ? entry.relationships.user.data : this.user
        if (!this.tasksConcur && !this.task) {
          newEntry.relationships[singularize(entry.task.type)] = { data: entry.task }
        } else if (this.task) {
          newEntry.relationships[singularize(this.task.type)] = { data: this.task }
          let currentEntry = this.$store.getters['time-entries/all'].filter(entry => entry.relationships.user && entry.relationships.user.data.id === this.$store.state.authentication.user.id && !entry.attributes.end)[0]
          if (currentEntry && entry && entry.id && entry.id === currentEntry.id) {
            getTimeRemaining(this.task)
          }
        }
        this.options.save(newEntry)
      })

      this.$emit('close')
    },
    selectTask (task) {
      let project = this.$store.getters['projects/byId']({ id: task.relationships.project.data.id })
      let client = this.$store.getters['clients/byId']({ id: project.relationships.client.data && project.relationships.client.data.id })
      let projectPath = task.attributes.path ? task.attributes.path.replace('|', ' | ') : '- - -'
      let defaultActivity = this.$store.getters['activities/byId']({ id: task.relationships.defaultActivity.data && task.relationships.defaultActivity.data.id })
      let activities = task.relationships.validActivities ? task.relationships.validActivities.data.map(data => this.$store.getters['activities/byId']({ id: data.id })) : []

      defaultActivity && !activities.some(activity => activity.id === defaultActivity.id) && activities.push(defaultActivity)
      this.activity = defaultActivity
      this.activities = activities
      this.client = client ? client.attributes.name : null
      this.projectPath = projectPath
      this.task = task
      this.activeNames = []
    },
    formatDateString: formatDateString,
    formatTimeString: formatTimeString
  }
}
</script>

<style lang='scss' scoped>
  @import '../styles/common.scss';
  .buttons {
    float:right;
  }
  .el-form {
    margin-bottom:60px;
  }
</style>

<style lang='scss'>
  .time-entry-dialog {

    .el-form-item{
      margin-bottom:5px !important;
    }

    .el-collapse {
      border: 1px solid rgb(220,223,230);
      border-radius: 4px;
      
      .el-collapse-item {

        .el-collapse-item__header {
          background-color: transparent;
          height: 40px;
          line-height: 40px;
          padding-left: 15px;
          border: none;
          font-size: 14px;
          color: #606266;

          i {
            line-height: 40px !important;
            transform: rotate(90deg);

            &.is-active {
              transform: rotate(-90deg);
            }
          }
        }
        .el-collapse-item__wrap {

          .tasks {
            margin: 0px 15px;

            .tasks-table {
              height: initial;
              max-height: calc(100vh - 427px);
            }
          }
        }
        &.placeholder {

          .el-collapse-item__header {
            color: #b5b5b5;
          }
        }
      }
    }
    .el-form {

      .date-time {
        
        
        .el-form-item__content {
          display: flex;

          > div {
            flex-grow: 1;
            margin-right: 5px;

            &:last-child {
              margin-right: 0px;
            }
          }
        }
      }
      .el-col {
        position: relative;

        &.activity, &.user {
          padding-right: 5px;

          .el-select {
            width: 100%;
          }
        }
      }
      .description-label, .submitted-label {
        text-align: right;
        padding-right: 12px;
      }
    }
    .warning-icon {
      color: #e6a23c;
      font-size: 24px;
      position: absolute;
      right: 5px;
      top: 20px;
      transform: translateY(-50%);
    }
    .el-collapse + .warning-icon, {
      right: 30px;
    }
    .activity .el-select + .warning-icon {
      right: 35px;
    }
    .el-checkbox + .warning-icon {
      left: 20px;
    }
  }
  .confirm-overlap-dialog {
    display: flex;
    justify-content: center;
    align-items: center;

    .el-dialog {
      padding-bottom: 10px;
      margin: 0px !important;

      .el-dialog__header {
        padding: 15px 15px 10px 15px;
        margin: 0px;
        
        .el-dialog__title {
          font-size: 18px;
        }
      }
      .el-dialog__body {
        padding: 10px 15px !important;

        span {
          display: block;
          padding: 0px 12px 0px 36px;
        }
        .warning {
          display: inline;
          color: #e6a23c;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          font-size: 24px !important;
          font-family: 'element-icons' !important;
          background-color: transparent;

          &::before {
            padding-left: 1px;
            content: "\E62E";
          }
        }
      }
      .el-dialog__footer {
        padding: 5px 15px 0px;
      }
    }
  }
</style>